/**
 * NUAGE — Interactions & UX
 * Unified scroll reveal, parallax, cart animations, filter response.
 */
(function(){
  'use strict';

  /* ==========================================================================
     1. UNIFIED SCROLL REVEAL — applied to every page globally
     ========================================================================== */
  function initScrollReveal() {
    var targets = document.querySelectorAll('.n-rv');
    if (!targets.length) return;

    /* Immediately reveal everything already in viewport so initial paint is instant */
    targets.forEach(function(el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight + 100) {
        el.classList.add('n-in');
      }
    });

    var obs = new IntersectionObserver(function(entries) {
      entries.forEach(function(e) {
        if (e.isIntersecting) {
          e.target.classList.add('n-in');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -5% 0px' });

    targets.forEach(function(el) {
      if (!el.classList.contains('n-in')) obs.observe(el);
    });
  }

  /* ==========================================================================
     2. HERO PARALLAX — subtle product image shift
     ========================================================================== */
  function initHeroParallax() {
    var heroMedia = document.querySelector('.n-hero-media img');
    if (!heroMedia) return;

    var hero = heroMedia.closest('.n-hero');
    if (!hero) return;

    var ticking = false;

    function update() {
      var rect = hero.getBoundingClientRect();
      var windowH = window.innerHeight;

      if (rect.bottom < 0 || rect.top > windowH) {
        ticking = false;
        return;
      }

      var scrolled = (windowH - rect.top) / (windowH + rect.height);
      var shift = (scrolled - 0.5) * 0.06; /* ±3% shift */
      heroMedia.style.transform = 'translate(-50%, calc(-50% + ' + (shift * 100) + '%))';
      ticking = false;
    }

    window.addEventListener('scroll', function() {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    update();
  }

  /* ==========================================================================
     3. CART DRAWER — stagger item entrance
     ========================================================================== */
  function initCartDrawerAnimation() {
    try {
      var observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(m) {
          if (m.target.classList.contains('active')) {
            var items = m.target.querySelectorAll('cart-drawer-items .cart-item');
            items.forEach(function(item, i) {
              item.style.opacity = '0';
              item.style.transform = 'translateX(20px)';
              item.style.transition = 'opacity 0.4s cubic-bezier(.16,1,.22,1), transform 0.5s cubic-bezier(.16,1,.22,1)';
              item.style.transitionDelay = (i * 0.08) + 's';
              requestAnimationFrame(function() {
                item.style.opacity = '1';
                item.style.transform = 'none';
              });
            });
          }
        });
      });

      var drawer = document.querySelector('cart-drawer');
      if (drawer) {
        observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }
    } catch(e) {}
  }

  /* ==========================================================================
     4. QUANTITY PULSE on change
     ========================================================================== */
  function initQuantityPulse() {
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('.quantity__button');
      if (!btn) return;

      var wrapper = btn.closest('.quantity');
      if (!wrapper) return;

      wrapper.style.transition = 'none';
      wrapper.style.transform = 'scale(1.06)';
      requestAnimationFrame(function() {
        wrapper.style.transition = 'transform 0.2s cubic-bezier(.22,1,.36,1)';
        wrapper.style.transform = 'scale(1)';
      });
    });
  }

  /* ==========================================================================
     5. FILTER RESPONSE — reduce debounce + instant visual
     ========================================================================== */
  function initFilterResponse() {
    document.addEventListener('change', function(e) {
      var checkbox = e.target.closest('.facets__checkbox, .facet-checkbox input');
      if (!checkbox) return;

      var label = checkbox.closest('label');
      if (label) {
        label.style.transition = 'color 0.15s';
        label.style.color = 'var(--n-sage-mid, #55604A)';
        setTimeout(function() { label.style.color = ''; }, 400);
      }
    });
  }

  /* ==========================================================================
     6. CART COUNT BOUNCE on add
     ========================================================================== */
  function initCartCountBounce() {
    try {
      var pubsub = window.PubSub || {};
      if (typeof pubsub.subscribe === 'function') {
        pubsub.subscribe('cart-update', function() {
          var bubble = document.querySelector('.cart-count-bubble');
          if (bubble && !bubble.classList.contains('is-empty')) {
            bubble.style.transition = 'none';
            bubble.style.transform = 'scale(1.3)';
            requestAnimationFrame(function() {
              bubble.style.transition = 'transform 0.35s cubic-bezier(.22,1,.36,1)';
              bubble.style.transform = 'scale(1)';
            });
          }
        });
      }
    } catch(e) {}
  }

  /* ==========================================================================
     7. REMOVE ANNOYING DAWN SCROLL-TRIGGER
     ========================================================================== */
  function killDawnScrollTrigger() {
    document.querySelectorAll('.scroll-trigger').forEach(function(el) {
      el.classList.remove('scroll-trigger--offscreen');
      el.classList.add('n-rv');
      if (el.dataset.cascade) el.removeAttribute('data-cascade');
    });
  }

  /* ==========================================================================
     8. NAVIGATION ACTIVE STATE
     ========================================================================== */
  function initNavActiveState() {
    var currentPath = window.location.pathname;
    var links = document.querySelectorAll('.n-nav-link');
    links.forEach(function(link) {
      var href = link.getAttribute('href');
      if (href && currentPath !== '/' && href.indexOf(currentPath) !== -1) {
        link.setAttribute('aria-current', 'page');
        link.style.color = 'var(--n-sage-mid, #55604A)';
      }
    });
  }

  /* ==========================================================================
     9. HEADER BACKDROP BLUR on scroll
     ========================================================================== */
  function initHeaderBlur() {
    var headerSticky = document.querySelector('.shopify-section-header-sticky');
    if (!headerSticky) return;

    var ticking = false;
    window.addEventListener('scroll', function() {
      if (ticking) return;
      requestAnimationFrame(function() {
        var scrolled = window.scrollY > 40;
        if (scrolled) {
          headerSticky.classList.add('animate');
        } else {
          headerSticky.classList.remove('animate');
        }
        ticking = false;
      });
      ticking = true;
    }, { passive: true });
  }

  /* ==========================================================================
     INIT
     ========================================================================== */
  function init() {
    initScrollReveal();
    initHeroParallax();
    initCartDrawerAnimation();
    initQuantityPulse();
    initFilterResponse();
    initCartCountBounce();
    initNavActiveState();
    initHeaderBlur();
    killDawnScrollTrigger();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
